import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { EmailAccountRow } from "./EmailAccountRow";
import { Email } from "../schema/email";

/**
 * Collapsible group for the additional (secondary) inboxes. Collapsed by default:
 * it shows just a count + expand toggle so the primary inbox stays the focus, and
 * reveals the individual account rows when expanded.
 */
export const SecondaryEmailsSection: FunctionComponent<{ emails: Email[] }> = ({ emails }) => {
	const { t } = useTranslation();

	const [expanded, setExpanded] = useState<boolean>(false);

	if (emails.length === 0) {
		return null;
	}

	return (
		<div className="flex flex-col gap-3">
			<button
				aria-expanded={expanded}
				className="flex cursor-pointer items-center gap-2 self-start px-1 text-[12.5px] font-semibold text-text-meta transition-colors hover:text-text"
				type="button"
				onClick={() => setExpanded((value) => !value)}
			>
				<Icon
					className={classNames("h-3 w-3 transition-transform", expanded ? "rotate-180" : "")}
					icon={faChevronDown}
				/>
				{expanded
					? t("account.connectedAccounts.hideOtherEmails")
					: t("account.connectedAccounts.showOtherEmails", { count: emails.length })}
			</button>

			{expanded && emails.map((email) => <EmailAccountRow key={email.id} email={email} />)}
		</div>
	);
};
